import { alpha } from '../../utils'
import { styled } from '../../utils/styled'
import MUIButton from '@mui/material/Button'
import { type ButtonScheme, type ButtonSize, type StyledButtonProps } from './types'
import { type CSSObject, type Theme } from '@mui/material/styles'
import Typography from '../Typography'
import Grid from '../Grid'

const getFilledBrandFocusStyles = (theme: Theme): CSSObject => ({
  outlineColor: theme.tokens.border.focus.brand.halo,
  boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.secondary} inset`
})

const getFilledWhiteFocusStyles = (theme: Theme): CSSObject => ({
  outlineColor: theme.tokens.border.focus.brand.haloSubtle,
  boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.primary} inset`
})

const getTonalFocusStyles = (theme: Theme): CSSObject => ({
  outlineColor: theme.tokens.border.focus.brand.haloSubtle,
  boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.primary} inset`
})

const getOutlinedTextFocusStyles = (theme: Theme): CSSObject => ({
  background: theme.tokens.background.main.default,
  outlineColor: theme.tokens.border.focus.brand.halo,
  boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.primary} inset`
})

const getSemanticFocusStyles = (theme: Theme): CSSObject => ({
  outlineColor: theme.tokens.border.focus.brand.halo,
  boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.secondary} inset`
})

/** Padding tokens: medium 40px min target; small −4px per edge → 32px for icon-only. */
const buttonPaddingTokens: Record<ButtonSize, { vertical: string; besideText: string; besideIcon: string }> = {
  medium: { vertical: '8px', besideText: '16px', besideIcon: '8px' },
  small: { vertical: '4px', besideText: '12px', besideIcon: '4px' }
}

export const calculateButtonPadding = (props: StyledButtonProps): string => {
  // you should have 16px of padding on the side where there's text, but not if there's an
  // icon present

  const pad = props.size === 'small' ? buttonPaddingTokens.small : buttonPaddingTokens.medium

  if (props.hasChildren) {
    const rightSidePadding = props.hasRightIcon ? pad.besideIcon : pad.besideText
    const leftSidePadding = props.hasLeftIcon ? pad.besideIcon : pad.besideText
    return `${pad.vertical} ${rightSidePadding} ${pad.vertical} ${leftSidePadding}`
  }
  return pad.vertical
}
export const ButtonInnerContainer = styled('div')`
  height: 24px;
  display: flex;
  flex-wrap: nowrap;
  text-wrap: nowrap;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 5px;
`
export const ButtonIconContainer = styled('div')`
  /* Auto layout */

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;

  width: 24px;
  height: 24px;
`
export const ButtonChildrenContainer = styled(Typography)`
  display: flex;
  align-items: center;
  text-align: center;
`

export const ButtonTooltipContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'fullWidth'
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'inline-flex',
  width: fullWidth === true ? '100%' : 'auto'
}))

export const StyledButton = styled(MUIButton, {
  shouldForwardProp: (prop) =>
    prop !== 'hasLeftIcon' &&
    prop !== 'hasRightIcon' &&
    prop !== 'hasChildren' &&
    prop !== 'buttonVariant' &&
    prop !== 'size'
})<StyledButtonProps>((props): any => ({
  fontWeight: '500',
  fontSize: '14px',
  lineHeight: '24px',
  padding: calculateButtonPadding(props),
  borderRadius: props.size === 'small' ? props.theme.borderRadius.small : props.theme.borderRadius.medium,
  minWidth: props.minWidth ?? (props.size === 'small' ? '32px' : '40px'),
  minHeight: props.minHeight ?? (props.size === 'small' ? '32px' : '40px'),
  textTransform: 'none',
  alignSelf: 'start',
  outlineColor: 'transparent',
  outlineWidth: '3px',
  outlineStyle: 'solid',
  '& .MuiPaper-root': {
    border: 'initial'
  },
  ...(props.buttonVariant === 'filled' && {
    ...StyledFilledButton(props.scheme, props.theme)
  }),
  ...(props.buttonVariant === 'tonal' && {
    ...StyledTonalButton(props.scheme, props.theme)
  }),
  ...(props.buttonVariant === 'outlined' && {
    ...StyledOutlineButton(props.scheme, props.theme)
  }),
  ...(props.buttonVariant === 'text' && {
    ...StyledTextButton(props.scheme, props.theme)
  }),
  ...(props.buttonVariant === 'semantic-destructive' && {
    ...StyledDestructiveButton(props.theme)
  }),
  ...(props.buttonVariant === 'semantic-warning' && {
    ...StyledWarningButton(props.theme)
  }),
  ...(props.buttonVariant === 'semantic-promo' && {
    ...StyledPromoButton(props.theme)
  })
}))

/** button / filled / brand — `scheme="light"` on default surfaces */
const StyledFilledBrandButton = (theme: Theme): CSSObject => ({
  color: theme.tokens.foreground.main.staticWhite,
  background: theme.tokens.background.brand.main.default,
  '&:hover': {
    background: theme.tokens.background.brand.main.hover
  },
  '&:focus': {
    background: theme.tokens.background.brand.main.default,
    ...getFilledBrandFocusStyles(theme)
  },
  '&:active': {
    background: theme.tokens.background.brand.main.pressed,
    outlineColor: 'transparent',
    boxShadow: 'none'
  },
  '&:disabled, &.Mui-disabled': {
    background: theme.tokens.background.brand.main.disabled,
    color: theme.tokens.foreground.main.disabled
  }
})

/** button / filled / white — `scheme="dark"` on brand/coloured surfaces */
const StyledFilledWhiteButton = (theme: Theme): CSSObject => ({
  color: theme.tokens.foreground.main.primary,
  background: theme.tokens.background.main.default,
  '&:hover': {
    background: theme.tokens.background.main.hover
  },
  '&:focus': {
    background: theme.tokens.background.main.default,
    ...getFilledWhiteFocusStyles(theme)
  },
  '&:active': {
    background: theme.tokens.background.main.pressed,
    outlineColor: 'transparent',
    boxShadow: 'none'
  },
  '&:disabled, &.Mui-disabled': {
    background: theme.tokens.background.main.disabled,
    color: theme.tokens.foreground.main.disabled
  }
})

const StyledFilledButton = (scheme: ButtonScheme, theme: Theme): CSSObject =>
  scheme === 'light' ? StyledFilledBrandButton(theme) : StyledFilledWhiteButton(theme)

/** button / tonal — `scheme="light"`; dark scheme keeps brand-surface treatment (not in alias spec) */
const StyledTonalButton = (scheme: ButtonScheme, theme: Theme): CSSObject => {
  if (scheme === 'light') {
    return {
      color: theme.tokens.foreground.main.primary,
      background: theme.tokens.background.tonal.soft.default,
      '&:hover': {
        background: theme.tokens.background.tonal.soft.hover
      },
      '&:focus': getTonalFocusStyles(theme),
      '&:active': {
        background: theme.tokens.background.tonal.soft.pressed,
        outlineColor: 'transparent',
        boxShadow: 'none'
      },
      '&:disabled, &.Mui-disabled': {
        background: theme.tokens.background.tonal.soft.disabled,
        color: theme.tokens.foreground.main.disabled
      }
    }
  }

  return {
    color: theme.tokens.foreground.main.staticWhite,
    background: theme.tokens.background.brand.main.hover,
    '&:hover': {
      background: theme.tokens.background.brand.main.pressed
    },
    '&:focus': {
      background: theme.tokens.background.brand.main.hover,
      outlineColor: theme.tokens.border.focus.brand.halo,
      boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.primary} inset`
    },
    '&:active': {
      background: theme.tokens.background.brand.main.pressed,
      outlineColor: 'transparent',
      boxShadow: 'none'
    },
    '&:disabled, &.Mui-disabled': {
      background: alpha(theme.tokens.foreground.main.staticWhite, 0.12),
      color: alpha(theme.tokens.foreground.main.staticWhite, 0.4)
    }
  }
}

/** button / outlined — `scheme="light"`; dark scheme keeps brand-surface treatment (not in alias spec) */
const StyledOutlineButton = (scheme: ButtonScheme, theme: Theme): CSSObject => {
  if (scheme === 'light') {
    return {
      color: theme.tokens.foreground.main.primary,
      boxShadow: `0px 0px 0px 1px ${theme.tokens.border.main.tonal} inset`,
      '&:hover': {
        background: theme.tokens.background.main.hover
      },
      '&:focus': getOutlinedTextFocusStyles(theme),
      '&:active': {
        background: theme.tokens.background.main.pressed,
        outlineColor: 'transparent',
        boxShadow: `0px 0px 0px 1px ${theme.tokens.border.main.tonal} inset`
      },
      '&:disabled, &.Mui-disabled': {
        background: theme.tokens.background.main.disabled,
        color: theme.tokens.foreground.main.disabled,
        boxShadow: `0px 0px 0px 1px ${theme.tokens.border.main.tonal} inset`
      }
    }
  }

  return {
    color: theme.tokens.foreground.main.staticWhite,
    boxShadow: `0px 0px 0px 1px ${alpha(theme.tokens.foreground.main.staticWhite, 0.25)} inset`,
    '&:hover': {
      background: theme.tokens.background.brand.main.hover,
      boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.halo} inset`
    },
    '&:focus': {
      background: theme.tokens.background.brand.main.hover,
      outlineColor: theme.tokens.border.focus.brand.halo,
      boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.primary} inset`
    },
    '&:active': {
      background: theme.tokens.background.brand.main.pressed,
      outlineColor: 'transparent',
      boxShadow: `0px 0px 0px 1px ${alpha(theme.tokens.foreground.main.staticWhite, 0.25)} inset`
    },
    '&:disabled, &.Mui-disabled': {
      background: alpha(theme.tokens.foreground.main.staticWhite, 0.12),
      color: alpha(theme.tokens.foreground.main.staticWhite, 0.4),
      boxShadow: `0px 0px 0px 1px ${alpha(theme.tokens.foreground.main.staticWhite, 0.12)} inset`
    }
  }
}

/** button / text — `scheme="light"`; dark scheme keeps brand-surface treatment (not in alias spec) */
const StyledTextButton = (scheme: ButtonScheme, theme: Theme): CSSObject => {
  if (scheme === 'light') {
    return {
      color: theme.tokens.foreground.main.primary,
      '&:hover': {
        background: theme.tokens.background.main.hover
      },
      '&:focus': getOutlinedTextFocusStyles(theme),
      '&:active': {
        background: theme.tokens.background.main.pressed,
        outlineColor: 'transparent',
        boxShadow: 'none'
      },
      '&:disabled, &.Mui-disabled': {
        background: theme.tokens.background.main.disabled,
        color: theme.tokens.foreground.main.disabled
      }
    }
  }

  return {
    color: theme.tokens.foreground.main.staticWhite,
    '&:hover': {
      background: theme.tokens.background.brand.main.hover
    },
    '&:focus': {
      background: theme.tokens.background.brand.main.hover,
      outlineColor: theme.tokens.border.focus.brand.halo,
      boxShadow: `0px 0px 0px 1px ${theme.tokens.border.focus.brand.primary} inset`
    },
    '&:active': {
      background: theme.tokens.background.brand.main.pressed,
      outlineColor: 'transparent',
      boxShadow: 'none'
    },
    '&:disabled, &.Mui-disabled': {
      background: 'transparent',
      color: alpha(theme.tokens.foreground.main.staticWhite, 0.4)
    }
  }
}

const StyledDestructiveButton = (theme: Theme): CSSObject => ({
  color: theme.tokens.foreground.main.staticWhite,
  background: theme.tokens.background.semantic.error.main.default,
  '&:hover': {
    background: theme.tokens.background.semantic.error.main.hover
  },
  '&:focus': {
    background: theme.tokens.background.semantic.error.main.default,
    ...getSemanticFocusStyles(theme)
  },
  '&:active': {
    background: theme.tokens.background.semantic.error.main.pressed,
    outlineColor: 'transparent',
    boxShadow: 'none'
  },
  '&:disabled, &.Mui-disabled': {
    background: theme.tokens.background.semantic.error.main.disabled,
    color: theme.tokens.foreground.main.disabled
  }
})

const StyledWarningButton = (theme: Theme): CSSObject => ({
  color: theme.tokens.foreground.main.staticWhite,
  background: theme.tokens.background.semantic.warning.main.default,
  '&:hover': {
    background: theme.tokens.background.semantic.warning.main.hover
  },
  '&:focus': {
    background: theme.tokens.background.semantic.warning.main.default,
    ...getSemanticFocusStyles(theme)
  },
  '&:active': {
    background: theme.tokens.background.semantic.warning.main.pressed,
    outlineColor: 'transparent',
    boxShadow: 'none'
  },
  '&:disabled, &.Mui-disabled': {
    background: theme.tokens.background.semantic.warning.main.disabled,
    color: theme.tokens.foreground.main.disabled
  }
})

const StyledPromoButton = (theme: Theme): CSSObject => ({
  color: theme.tokens.foreground.main.staticWhite,
  background: theme.tokens.background.semantic.success.main.default,
  '&:hover': {
    background: theme.tokens.background.semantic.success.main.hover
  },
  '&:focus': {
    background: theme.tokens.background.semantic.success.main.default,
    ...getSemanticFocusStyles(theme)
  },
  '&:active': {
    background: theme.tokens.background.semantic.success.main.pressed,
    outlineColor: 'transparent',
    boxShadow: 'none'
  },
  '&:disabled, &.Mui-disabled': {
    background: theme.tokens.background.semantic.success.main.disabled,
    color: theme.tokens.foreground.main.disabled
  }
})
